/**
 * Persists checkout state in sessionStorage with expiration.
 * Restores state on page reload and syncs step with URL query param.
 */

const SESSION_TTL_MS = 30 * 60 * 1000; // 30 minutes

// Reason: data is a generic checkout state container utilisé par plusieurs flows
// (events, memberships, etc.) avec des shapes différentes. Le typage strict serait
// fait côté caller via cast — un Record<string, any> ici garde l'API simple.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type CheckoutData = Record<string, any>;

interface CheckoutSessionData {
  step: number;
  data: CheckoutData;
  timestamp: number;
}

export function useCheckoutSession(sessionKey: string) {
  const route = useRoute();
  const router = useRouter();

  function getStorageKey() {
    return `checkout:${sessionKey}`;
  }

  /** Sync step to URL query param */
  function syncStepToUrl(step: number) {
    if (!import.meta.client) return;
    const currentStep = Number(route.query.step) || 1;
    const targetStep = step > 1 ? step : undefined;
    if (currentStep !== (targetStep || 1)) {
      router.replace({ query: { ...route.query, step: targetStep ? String(targetStep) : undefined } });
    }
  }

  /** Save current checkout state */
  function save(step: number, data: CheckoutData) {
    if (!import.meta.client) return;
    const session: CheckoutSessionData = {
      step,
      data,
      timestamp: Date.now(),
    };
    try {
      sessionStorage.setItem(getStorageKey(), JSON.stringify(session));
    } catch {
      // intentional empty: storage quota errors are non-fatal
    }
    syncStepToUrl(step);
  }

  /** Restore checkout state, returns null if expired or missing */
  function restore(): { step: number; data: CheckoutData; expired?: boolean } | null {
    if (!import.meta.client) return null;
    try {
      const raw = sessionStorage.getItem(getStorageKey());
      if (!raw) return null;
      const session: CheckoutSessionData = JSON.parse(raw);
      // Check expiration
      if (Date.now() - session.timestamp > SESSION_TTL_MS) {
        clear();
        return { step: session.step, data: {}, expired: true };
      }
      return { step: session.step, data: session.data };
    } catch {
      return null;
    }
  }

  /** Get step from URL query param */
  function getStepFromUrl(): number {
    return Number(route.query.step) || 1;
  }

  /** Clear session */
  function clear() {
    if (!import.meta.client) return;
    try {
      sessionStorage.removeItem(getStorageKey());
    } catch {
      // intentional empty: storage errors are non-fatal
    }
    // Remove step from URL
    if (route.query.step) {
      router.replace({ query: { ...route.query, step: undefined } });
    }
  }

  /** Check if current session is expired (for live monitoring) */
  function isExpired(): boolean {
    if (!import.meta.client) return false;
    try {
      const raw = sessionStorage.getItem(getStorageKey());
      if (!raw) return false;
      const session: CheckoutSessionData = JSON.parse(raw);
      return Date.now() - session.timestamp > SESSION_TTL_MS;
    } catch {
      return false;
    }
  }

  return { save, restore, getStepFromUrl, clear, isExpired, syncStepToUrl };
}
